'use client';

import { useId, useRef, useState } from 'react';
import { TagList } from '@/components/common/TagList';
import { DetailComparisonPanel } from '@/components/redesign/DetailComparisonPanel';
import { ToggleButton } from './ToggleButton';
import type { AccordionItemView, AccordionLayout } from './types';

type Props = {
  item: AccordionItemView;
  layout?: AccordionLayout;
};

export function AccordionCard({ item, layout = 'details' }: Props) {
  const [open, setOpen] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const innerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const isIncludes = layout === 'includes';
  const canExpand = isIncludes
    ? Boolean(item.includesHeading || item.includeItems.length > 0)
    : item.tags.length > 0 || item.detailRows.length > 0;
  const hasDetailPanel = Boolean(
    item.detailsIntro ||
    item.leftColumnTitle ||
    item.rightColumnTitle ||
    item.detailRows.length > 0,
  );

  const handleToggle = () => {
    const inner = innerRef.current;
    if (open) {
      setOpen(false);
      setMaxHeight(0);
      return;
    }

    setOpen(true);
    setMaxHeight(inner?.scrollHeight ?? 0);
  };

  const titleClassName =
    'flex-1 text-[20px] font-semibold leading-[1.4] text-black md:text-[24px]';

  return (
    <div className="flex w-full flex-col gap-2 rounded-[10px] bg-[#f3f3f3] p-4 md:p-[25px]">
      {canExpand ? (
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex w-full items-start justify-between gap-4 text-left"
        >
          <h3 className={titleClassName}>{item.itemTitle}</h3>
          <ToggleButton open={open} />
        </button>
      ) : (
        item.itemTitle && <h3 className={titleClassName}>{item.itemTitle}</h3>
      )}

      {item.summary ? (
        <p className="w-full text-[16px] font-light leading-[1.5] text-black md:text-[18px]">
          {item.summary}
        </p>
      ) : null}

      {isIncludes ? <TagList labels={item.tags} bordered={false} /> : null}

      {canExpand ? (
        <div
          id={panelId}
          aria-hidden={!open}
          className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
          style={{ maxHeight }}
        >
          <div
            ref={innerRef}
            className={
              isIncludes ? 'pt-4 md:pt-6' : 'flex flex-col gap-[44px] pt-3'
            }
          >
            {isIncludes ? (
              <DetailComparisonPanel
                layout="list"
                heading={item.includesHeading}
                intro=""
                leftColumnTitle=""
                rightColumnTitle=""
                rows={item.includeItems.map((text) => ({ label: '', text }))}
              />
            ) : (
              <>
                <TagList labels={item.tags} bordered={false} />
                {hasDetailPanel ? (
                  <DetailComparisonPanel
                    intro={item.detailsIntro}
                    leftColumnTitle={item.leftColumnTitle}
                    rightColumnTitle={item.rightColumnTitle}
                    rows={item.detailRows}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
